import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle, Clock, Activity, PieChart, Scale, Timer, UserCheck, Landmark } from 'lucide-react';
import statutoryService, {
    type StatInstance,
    type ComplianceAnalytics,
    type ClassificationRow,
} from '../../services/statutoryService';
import { litInstanceService, type LitInstance } from '../../services/litInstanceService';
import { ComplianceInsightsSkeleton } from '../common/InsightsSkeleton';
import NoDataFound from '../common/NoDataFound';
import { usePermission, NOT_AUTHORISED_MSG } from '../../contexts/PermissionContext';

const STATUS_COLOR: Record<string, string> = {
    PENDING: '#94a3b8', IN_PROGRESS: '#3b82f6', COMPLETED: '#10b981', CANCELLED: '#f43f5e',
};
const STATUS_LABEL: Record<string, string> = {
    PENDING: 'Pending', IN_PROGRESS: 'In Progress', COMPLETED: 'Completed', CANCELLED: 'Cancelled',
};

const KpiCard = ({ label, value, sub, icon: Icon, iconBg, iconColor }: any) => (
    <div className="relative bg-white rounded-2xl flex items-center gap-4 px-5 py-5 cursor-default overflow-hidden"
        style={{ border: '1px solid #e8ecf4', boxShadow: '0 4px 20px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)', transition: 'box-shadow 0.2s ease, transform 0.2s ease' }}
        onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.boxShadow = '0 8px 28px rgba(0,0,0,0.11), 0 2px 6px rgba(0,0,0,0.06)'; (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-1px)'; }}
        onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 20px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)'; (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)'; }}>
        {iconColor && <div className="absolute bottom-0 right-0 w-24 h-24 rounded-full pointer-events-none" style={{ background: `radial-gradient(circle, ${iconColor}0d 0%, transparent 70%)`, transform: 'translate(30%, 30%)' }} />}
        <div className="shrink-0 p-3 rounded-xl" style={{ background: iconBg, boxShadow: `0 2px 10px ${iconColor}28` }}>
            <Icon size={20} style={{ color: iconColor }} />
        </div>
        <div className="min-w-0 flex-1">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 truncate mb-1">{label}</p>
            <span className="text-[2rem] font-extrabold text-slate-800 leading-none tabular-nums">{value}</span>
            {sub && <p className="text-[10px] text-slate-400 mt-1 truncate">{sub}</p>}
        </div>
    </div>
);


// ── Ageing bar ────────────────────────────────────────────────────────────────
// Open obligations laid out on a single time axis, oldest-overdue on the left.
// One stacked bar rather than seven KPI tiles: the shape of the backlog is the
// message, and seven equal-weight numbers hide it.
const AGE_BANDS = [
    { key: 'overdue_30_plus', label: '30+ days overdue', color: '#b91c1c' },
    { key: 'overdue_8_30',    label: '8–30 days overdue', color: '#ef4444' },
    { key: 'overdue_1_7',     label: '1–7 days overdue',  color: '#f97316' },
    { key: 'due_today',       label: 'Due today',         color: '#f59e0b' },
    { key: 'due_next_7',      label: 'Due in 7 days',     color: '#eab308' },
    { key: 'due_next_30',     label: 'Due in 30 days',    color: '#3b82f6' },
    { key: 'due_later',       label: 'Later',             color: '#94a3b8' },
] as const;

const AgeingBar = ({ ageing }: { ageing: ComplianceAnalytics['ageing'] }) => {
    const bands = AGE_BANDS.map(b => ({ ...b, count: (ageing as any)[b.key] ?? 0 }));
    const total = bands.reduce((a, b) => a + b.count, 0);
    return (
        <div className="rounded-2xl overflow-hidden flex flex-col"
            style={{ background: '#ffffff', border: '1px solid #e8ecf4', boxShadow: '0 4px 20px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)' }}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                <div className="flex items-center gap-2">
                    <div className="p-1.5 rounded-lg" style={{ background: '#f9731612' }}><Timer size={14} style={{ color: '#f97316' }} /></div>
                    <span className="text-xs font-bold text-slate-700">Open Obligations by Age</span>
                </div>
                <span className="text-[10px] text-slate-400 tabular-nums">{total} open</span>
            </div>
            <div className="px-4 py-4">
                {total === 0 ? (
                    <p className="text-xs text-slate-400 italic">Nothing open — every obligation is closed.</p>
                ) : (
                    <>
                        <div className="flex w-full h-3 rounded-full overflow-hidden mb-3" style={{ background: '#f1f5f9' }}>
                            {bands.filter(b => b.count > 0).map(b => (
                                <div key={b.key} style={{ width: `${(b.count / total) * 100}%`, background: b.color }} title={`${b.label}: ${b.count}`} />
                            ))}
                        </div>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-1.5">
                            {bands.map(b => (
                                <div key={b.key} className="flex items-center gap-2 min-w-0">
                                    <span className="shrink-0 w-2 h-2 rounded-full" style={{ background: b.color }} />
                                    <span className="text-[11px] text-slate-500 truncate flex-1">{b.label}</span>
                                    <span className={`text-[11px] tabular-nums ${b.count > 0 ? 'font-bold text-slate-700' : 'text-slate-300'}`}>{b.count}</span>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

// ── Pending with whom ─────────────────────────────────────────────────────────
// Named people, not seats: "pending with the Estate Officer" is only actionable
// if you know who sits in that chair today. Sorted by overdue first.
const PendingWithTable = ({ rows }: { rows: ComplianceAnalytics['pending_with'] }) => (
    <div className="rounded-2xl overflow-hidden flex flex-col h-full"
        style={{ background: '#ffffff', border: '1px solid #e8ecf4', boxShadow: '0 4px 20px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)' }}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <div className="flex items-center gap-2">
                <div className="p-1.5 rounded-lg" style={{ background: '#6366f112' }}><UserCheck size={14} style={{ color: '#6366f1' }} /></div>
                <span className="text-xs font-bold text-slate-700">Pending With</span>
            </div>
            <span className="text-[10px] text-slate-400 tabular-nums">{rows.length} holder{rows.length === 1 ? '' : 's'}</span>
        </div>
        <div className="px-2 py-2 overflow-auto" style={{ maxHeight: 300 }}>
            {rows.length === 0 ? (
                <p className="text-xs text-slate-400 italic px-2 py-3">No open obligation steps.</p>
            ) : rows.map((r, i) => (
                <div key={i} className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-slate-50">
                    <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-slate-700 truncate">{r.owner_name || 'Unassigned'}</p>
                        <p className="text-[10px] text-slate-400 truncate">{r.seat} · {r.role === 'APPROVE' ? 'Approves' : 'Performs'}</p>
                    </div>
                    {r.overdue_steps > 0 && (
                        <span className="shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full tabular-nums"
                            style={{ background: '#fef2f2', color: '#b91c1c' }}>{r.overdue_steps} overdue</span>
                    )}
                    <span className="shrink-0 text-sm font-extrabold text-slate-700 tabular-nums w-8 text-right">{r.open_steps}</span>
                </div>
            ))}
        </div>
    </div>
);

// ── Classification rollup ─────────────────────────────────────────────────────
// Split by law / department / criticality. `overdue` (open past due) and
// `late_closed` (closed, but discharged after the due date) are shown as
// separate columns — collapsing them would hide a control that always finishes
// but never finishes on time.
const ClassificationTable = ({ title, rows, icon: Icon, accent }: {
    title: string; rows: ClassificationRow[]; icon: any; accent: string;
}) => {
    const max = Math.max(...rows.map(r => r.total), 1);
    return (
        <div className="rounded-2xl overflow-hidden flex flex-col h-full"
            style={{ background: '#ffffff', border: '1px solid #e8ecf4', boxShadow: '0 4px 20px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)' }}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                <div className="flex items-center gap-2">
                    <div className="p-1.5 rounded-lg" style={{ background: `${accent}12` }}><Icon size={14} style={{ color: accent }} /></div>
                    <span className="text-xs font-bold text-slate-700">{title}</span>
                </div>
                <div className="flex items-center gap-3 text-[9px] font-bold uppercase tracking-wider text-slate-400">
                    <span>Overdue</span><span>Late</span><span>Total</span>
                </div>
            </div>
            <div className="px-2 py-2 overflow-auto" style={{ maxHeight: 300 }}>
                {rows.length === 0 ? (
                    <p className="text-xs text-slate-400 italic px-2 py-3">Nothing to group.</p>
                ) : rows.map((r, i) => (
                    <div key={i} className="px-2 py-2 rounded-lg hover:bg-slate-50">
                        <div className="flex items-center gap-3">
                            <span className="text-xs text-slate-600 truncate flex-1" title={r.label}>{r.label}</span>
                            <span className={`text-[11px] tabular-nums w-8 text-right font-bold ${r.overdue > 0 ? 'text-red-600' : 'text-slate-300'}`}>{r.overdue}</span>
                            <span className={`text-[11px] tabular-nums w-8 text-right font-bold ${r.late_closed > 0 ? 'text-amber-600' : 'text-slate-300'}`}>{r.late_closed}</span>
                            <span className="text-[11px] tabular-nums w-8 text-right font-bold text-slate-700">{r.total}</span>
                        </div>
                        <div className="flex w-full h-1 rounded-full overflow-hidden mt-1.5" style={{ background: '#f1f5f9' }}>
                            <div style={{ width: `${(r.completed / max) * 100}%`, background: '#10b981' }} title={`${r.completed} completed`} />
                            <div style={{ width: `${(r.overdue / max) * 100}%`, background: '#ef4444' }} title={`${r.overdue} overdue`} />
                            <div style={{ width: `${(r.open_not_due / max) * 100}%`, background: '#cbd5e1' }} title={`${r.open_not_due} open, not yet due`} />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const DistributionWidget = ({ title, data, icon: Icon, total, accentColor = '#6366f1', className = '' }: any) => {
    const maxCount = Math.max(...data.map((d: any) => d.count), 1);
    return (
        <div className={`rounded-2xl overflow-hidden flex flex-col ${className}`}
            style={{ background: '#ffffff', border: '1px solid #e8ecf4', boxShadow: '0 4px 20px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)' }}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-white">
                <div className="flex items-center gap-2">
                    <div className="p-1.5 rounded-lg" style={{ background: `${accentColor}12` }}>
                        <Icon size={12} style={{ color: accentColor }} />
                    </div>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">{title}</span>
                </div>
                {total > 0 && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full tabular-nums" style={{ background: `${accentColor}12`, color: accentColor }}>{total}</span>}
            </div>
            {data.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-6 italic">No data.</p>
            ) : (
                <ul className="divide-y divide-slate-50 flex-1">
                    {data.map((item: any, idx: number) => {
                        const color = item.color || '#6366f1';
                        const barPct = item.count > 0 ? Math.round((item.count / maxCount) * 100) : 0;
                        const pct = total > 0 ? Math.round((item.count / total) * 100) : 0;
                        return (
                            <li key={idx} className="grid items-center gap-3 px-4 py-2.5 transition-colors duration-100"
                                style={{ gridTemplateColumns: '1fr 110px 3.5rem' }}
                                onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                                <span className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide truncate">{item.label}</span>
                                <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'linear-gradient(to bottom, #f1f5f9, #e2e8f0)' }}>
                                    <div className="h-1.5 rounded-full transition-all duration-500"
                                        style={{ width: barPct > 0 ? `${barPct}%` : '0%', background: `linear-gradient(90deg, ${color}bb, ${color})`, boxShadow: barPct > 10 ? `0 1px 4px ${color}55` : 'none' }} />
                                </div>
                                <div className="flex items-center justify-end gap-1">
                                    <span className="text-[12px] font-extrabold tabular-nums" style={{ color: item.count > 0 ? color : '#94a3b8' }}>{item.count}</span>
                                    <span className="text-[10px] text-slate-400">({pct}%)</span>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

const TrendChart = ({ months, trendMax }: any) => (
    <div className="rounded-2xl overflow-hidden flex flex-col"
        style={{ background: '#ffffff', border: '1px solid #e8ecf4', boxShadow: '0 4px 20px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)' }}>
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 bg-white">
            <div className="p-1.5 rounded-lg" style={{ background: '#6366f112' }}><Activity size={12} style={{ color: '#6366f1' }} /></div>
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Obligations Invoked — Last 6 Months</span>
        </div>
        <div className="flex-1 px-4 py-3 flex items-end justify-between gap-3" style={{ minHeight: '220px' }}>
            {months.map((mm: any) => (
                <div key={mm.key} className="flex-1 flex flex-col items-center justify-end h-full">
                    <span className="text-[10px] font-semibold text-slate-500 mb-1">{mm.count}</span>
                    <div className="w-full rounded-t transition-all" style={{ background: 'linear-gradient(to top, #6366f1, #818cf8)', height: `${(mm.count / trendMax) * 100}%`, minHeight: mm.count ? '4px' : '0', boxShadow: mm.count ? '0 2px 8px #6366f133' : 'none' }} />
                    <span className="text-[10px] text-slate-400 mt-1.5">{mm.label}</span>
                </div>
            ))}
        </div>
    </div>
);

/* ── Section header — separates Obligations vs Litigations ────────────── */
const SectionHeader = ({ icon: Icon, label, accent, hint }: { icon: any; label: string; accent: string; hint?: string }) => (
    <div className="flex items-center gap-2 pt-1">
        <div className="p-1.5 rounded-lg" style={{ background: `${accent}14` }}>
            <Icon size={13} style={{ color: accent }} />
        </div>
        <span className="text-[12px] font-bold uppercase tracking-wider" style={{ color: accent }}>{label}</span>
        {hint && <span className="text-[10px] text-slate-400 truncate">— {hint}</span>}
    </div>
);

const ComplianceInsights = () => {
    const { allowed: canView, loading: permLoading } = usePermission('COM_COMPLIANCE_REGISTER', 'view');
    const [instances, setInstances] = useState<StatInstance[]>([]);
    const [litigations, setLitigations] = useState<LitInstance[]>([]);
    const [obligationCount, setObligationCount] = useState(0);
    const [analytics, setAnalytics] = useState<ComplianceAnalytics | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            setLoading(true);
            const [inst, lit, obCount, an] = await Promise.all([
                statutoryService.listInstances('all').catch(() => []),
                litInstanceService.getAll().catch(() => []),
                statutoryService.countObligations().catch(() => 0),
                // Server-side rollups. Null on failure rather than a throw, so a
                // permission gap on one endpoint cannot blank the whole screen.
                statutoryService.getAnalytics().catch(() => null),
            ]);
            setInstances(inst); setLitigations(lit.filter(l => l.is_active === 1));
            setObligationCount(obCount); setAnalytics(an); setLoading(false);
        })();
    }, []);

    const m = useMemo(() => {
        const now = new Date(); const today = new Date(); today.setHours(0, 0, 0, 0);
        const in7 = new Date(today.getTime() + 7 * 86_400_000);
        const asDate = (d?: string | null) => { if (!d) return null; const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
        const total = instances.length;
        const completed = instances.filter(i => i.status === 'COMPLETED').length;
        const health = total ? Math.round((completed / total) * 100) : 0;
        const isOpen = (i: StatInstance) => i.status !== 'COMPLETED' && i.status !== 'CANCELLED';
        const overdue = instances.filter(i => { const d = asDate(i.due_date); return isOpen(i) && d !== null && d < today; }).length;
        const upcoming = instances.filter(i => { const d = asDate(i.due_date); return isOpen(i) && d !== null && d >= today && d <= in7; }).length;
        const litTotal = litigations.length;
        const pendingLit = litigations.filter(l => l.status !== 'Closed').length;
        const dist = (['PENDING', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED'] as const)
            .map(s => ({ label: STATUS_LABEL[s], count: instances.filter(i => i.status === s).length, color: STATUS_COLOR[s] }))
            .filter(d => d.count > 0);
        const months: { key: string; label: string; count: number }[] = [];
        for (let k = 5; k >= 0; k--) {
            const d = new Date(now.getFullYear(), now.getMonth() - k, 1);
            months.push({ key: `${d.getFullYear()}-${d.getMonth()}`, label: d.toLocaleString('en-IN', { month: 'short' }), count: 0 });
        }
        const idx = new Map(months.map((mm, i) => [mm.key, i]));
        for (const i of instances) {
            if (!i.initiated_on) continue;
            const d = new Date(i.initiated_on);
            const key = `${d.getFullYear()}-${d.getMonth()}`;
            if (idx.has(key)) months[idx.get(key)!].count += 1;
        }
        return { total, completed, health, upcoming, overdue, pendingLit, litTotal, dist, months };
    }, [instances, litigations]);

    const trendMax = Math.max(1, ...m.months.map(x => x.count));

    // ── Not-authorised gate ─────────────────────────────────────────────────
    if (!permLoading && !canView) {
        return (
            <NoDataFound title="Not authorised" message="You do not have access to 'COM_INSIGHTS'." />
        );
    }

    return (
        <div className="netbody-card flex flex-col h-full min-h-[500px]">
            <div className="netbody-content">
                <div className="scroll-container-nofilters flex-1 overflow-hidden">
                    {loading && <ComplianceInsightsSkeleton />}
                    {!loading && (
                        <div className="py-4 space-y-5">
                            {/* ── Obligations ─────────────────────────────── */}
                            <SectionHeader icon={CheckCircle} label="Obligations" accent="#10b981"
                                hint="Rolled-out obligation instances (from the register) — not the register definitions." />
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                <KpiCard label="Total Obligations"
                                    value={`${obligationCount} · ${m.total}`}
                                    sub={`${obligationCount} obligation${obligationCount === 1 ? '' : 's'} · ${m.total} instance${m.total === 1 ? '' : 's'} rolled out`}
                                    icon={CheckCircle} iconBg="#ecfdf5" iconColor="#10b981" />
                                <KpiCard label="Upcoming (7 days)" value={m.upcoming} sub="Instances due this week" icon={Clock} iconBg="#fffbeb" iconColor="#f59e0b" />
                                <KpiCard label="Overdue (Backlog)" value={m.overdue} sub="Instances not closed past due date" icon={AlertTriangle} iconBg="#fef2f2" iconColor="#ef4444" />
                                <KpiCard label="Completed" value={m.completed} sub={`${m.health}% of ${m.total} instances complete`} icon={CheckCircle} iconBg="#eff6ff" iconColor="#3b82f6" />
                            </div>
                            <div className="flex gap-4 items-stretch">
                                <div className="flex-1 min-w-0">
                                    <TrendChart months={m.months} trendMax={trendMax} />
                                </div>
                                <div className="shrink-0" style={{ width: 'calc(25% - 0.5rem)' }}>
                                    <DistributionWidget title="Instance Status Distribution" data={m.dist} icon={PieChart} total={m.total} accentColor="#3b82f6" className="h-full" />
                                </div>
                            </div>

                            {/* ── Timeliness & backlog ────────────────────── */}
                            {/* The two dates the whole register runs on: due_date
                                (when it was owed) and compliance_date (when it was
                                actually discharged). Everything below derives from
                                those, so it reconciles with the tiles above. */}
                            {analytics && (
                                <>
                                    <SectionHeader icon={Timer} label="Timeliness & Backlog" accent="#f97316"
                                        hint="Derived from due date vs the date compliance was actually discharged. 'Overdue' is open past due; 'Late' is closed, but discharged after the due date." />
                                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                        <KpiCard label="On-Time Rate"
                                            value={analytics.timeliness.on_time_pct === null ? '—' : `${analytics.timeliness.on_time_pct}%`}
                                            sub={`${analytics.timeliness.on_time} of ${analytics.timeliness.closed_total} closed on or before due date`}
                                            icon={CheckCircle} iconBg="#ecfdf5" iconColor="#10b981" />
                                        <KpiCard label="Closed Late" value={analytics.timeliness.late}
                                            sub="Discharged after the due date"
                                            icon={AlertTriangle} iconBg="#fffbeb" iconColor="#f59e0b" />
                                        <KpiCard label="Overdue Now"
                                            value={analytics.ageing.overdue_30_plus + analytics.ageing.overdue_8_30 + analytics.ageing.overdue_1_7}
                                            sub={`${analytics.ageing.overdue_30_plus} of them over 30 days old`}
                                            icon={AlertTriangle} iconBg="#fef2f2" iconColor="#ef4444" />
                                        <KpiCard label="Avg Days Early" value={analytics.timeliness.avg_days_early}
                                            sub="Mean lead time before the due date"
                                            icon={Clock} iconBg="#eff6ff" iconColor="#3b82f6" />
                                    </div>
                                    <div className="flex gap-4 items-stretch flex-col lg:flex-row">
                                        <div className="flex-1 min-w-0"><AgeingBar ageing={analytics.ageing} /></div>
                                        <div className="flex-1 min-w-0"><PendingWithTable rows={analytics.pending_with} /></div>
                                    </div>

                                    {/* ── Classification ──────────────────────── */}
                                    <SectionHeader icon={Landmark} label="Classification" accent="#6366f1"
                                        hint="The same obligation book split by the source Act, the owning department and criticality." />
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                        <ClassificationTable title="By Source Act / Regulation" rows={analytics.classification.by_law} icon={Landmark} accent="#6366f1" />
                                        <ClassificationTable title="By Department" rows={analytics.classification.by_department} icon={Activity} accent="#0ea5e9" />
                                        <ClassificationTable title="By Criticality" rows={analytics.classification.by_criticality} icon={AlertTriangle} accent="#ef4444" />
                                        <ClassificationTable title="By Obligation Type" rows={analytics.classification.by_obligation_type} icon={PieChart} accent="#8b5cf6" />
                                    </div>
                                </>
                            )}

                            {/* ── Litigations ─────────────────────────────── */}
                            <SectionHeader icon={Scale} label="Litigations" accent="#8b5cf6"
                                hint="Legal cases tracked separately from obligations." />
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                <KpiCard label="Litigations Pending" value={m.pendingLit} sub={`of ${m.litTotal} total case${m.litTotal === 1 ? '' : 's'}`} icon={Scale} iconBg="#f5f3ff" iconColor="#8b5cf6" />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ComplianceInsights;
